import type {
  AlertRule,
  PlatformEvent,
  RuleInput,
  TenantUser,
} from "@energyplatform/client";
import { locale, t } from "./i18n.ts";
import { formatNumber } from "./format.ts";
import type { IconName } from "./icons.tsx";

// Shared wording of events and alert rules (prototype "Zdarzenia"), so the list, the event page, the wizard and the
// bell say the same thing.

export const KIND_ICON: Record<string, IconName> = {
  noData: "clock",
  integration: "plug",
  above: "gauge",
  below: "gauge",
  outside: "gauge",
};
export const STAGE_CHIP: Record<string, string> = {
  inProgress: "acc",
  closed: "stale",
  toVerify: "warn",
};
export const PRIORITY_CHIP: Record<number, string> = {
  1: "crit",
  2: "warn",
  3: "",
};

/** The rule an event came from, when a person set it. */
export function ruleOf(
  e: PlatformEvent,
  rules: readonly AlertRule[] | undefined,
): AlertRule | undefined {
  return e.ruleKey.startsWith("rule:")
    ? rules?.find((r) => `rule:${r.id}` === e.ruleKey)
    : undefined;
}

/** What happened, in words: built-in events by kind, rule events by the rule's name. */
export function eventTitle(
  e: PlatformEvent,
  rule: AlertRule | undefined,
): string {
  if (rule) {
    return t("events.title.rule", { rule: rule.name, name: e.subjectName });
  }
  return t(`events.title.${e.kind}`, { name: e.subjectName });
}

const num = (v: number | null | undefined) =>
  formatNumber(v ?? null, locale(), Math.abs(v ?? 0) < 10 ? 2 : 1);

/** The rule's condition as a sentence (prototype condText). */
export function conditionText(
  r: Pick<
    RuleInput,
    "kind" | "threshold" | "threshold2" | "minutes" | "window" | "hysteresis"
  >,
  unit = "",
): string {
  const u = unit ? ` ${unit}` : "";
  const time =
    r.minutes === 0
      ? t("events.rule.instant")
      : t("events.rule.forMinutes", { minutes: r.minutes });
  const when =
    r.window === "always" ? "" : ` · ${t(`events.window.${r.window}`)}`;
  const hyst = r.hysteresis
    ? ` · ${t("events.rule.hysteresisText", { value: num(r.hysteresis) })}`
    : "";
  switch (r.kind) {
    case "noData":
      return `${t("events.rule.noDataText", { minutes: Math.max(r.minutes, 15) })}${when}`;
    case "outside":
      return `${t("events.rule.outsideText", { from: `${num(r.threshold)}${u}`, to: `${num(r.threshold2)}${u}` })} ${time}${hyst}${when}`;
    default:
      return `${t(`events.rule.${r.kind}Text`, { value: `${num(r.threshold)}${u}` })} ${time}${hyst}${when}`;
  }
}

/** Who a rule watches: its point, or the points of a medium in a place. */
export function subjectText(
  r: Pick<RuleInput, "pointId" | "nodeId" | "medium">,
  pointName: (id: string) => string,
  nodeName: (id: string) => string,
): string {
  if (r.pointId) {
    return pointName(r.pointId);
  }
  return t("events.rule.place", {
    medium: r.medium ? t(`medium.${r.medium}`) : t("events.rule.anyMedium"),
    place: r.nodeId ? nodeName(r.nodeId) : "",
  });
}

export function userName(
  users: readonly TenantUser[] | undefined,
  idOrName: string | null | undefined,
): string {
  if (!idOrName) {
    return "—";
  }
  return users?.find((u) => u.id === idOrName)?.name ?? idOrName;
}

/** The escalation of an event now, as the prototype's card says it: when, to whom, or why not. */
export function escalationText(
  e: PlatformEvent,
  rule: AlertRule | undefined,
  users: readonly TenantUser[] | undefined,
  now = Date.now(),
): [string, string] {
  if (e.stage === "closed") {
    return ["—", t("events.esc.closed")];
  }
  if (!rule?.escalateAfter || !rule.escalateTo) {
    return [t("events.esc.none"), t("events.esc.noRule")];
  }
  if (e.escalatedAt) {
    return [t("events.esc.done"), userName(users, rule.escalateTo)];
  }
  if (e.acknowledgedAt) {
    return [t("events.esc.held"), t("events.esc.acknowledged")];
  }
  const left = Math.max(
    1,
    Math.ceil(
      (new Date(e.startedAt).getTime() + rule.escalateAfter * 60_000 - now) /
        60_000,
    ),
  );
  return [
    t("events.esc.in", { minutes: left }),
    userName(users, rule.escalateTo),
  ];
}

/** A new rule's starting values, optionally for a point (prototype "Dodaj regułę dla punktu"). */
export function newRule(pointId: string | null = null, name = ""): RuleInput {
  return {
    name,
    kind: "above",
    pointId,
    nodeId: null,
    medium: null,
    threshold: null,
    threshold2: null,
    hysteresis: null,
    minutes: 15,
    window: "always",
    priority: 2,
    enabled: true,
    recipients: [],
    escalateAfter: null,
    escalateTo: null,
    autoClose: false,
    remindHours: null,
    test: false,
  };
}

export function ruleToInput(r: AlertRule): RuleInput {
  const {
    id: _id,
    testUntil,
    mutedUntil: _muted,
    createdAt: _created,
    createdBy: _by,
    ...input
  } = r;
  return {
    ...input,
    test: testUntil !== null && new Date(testUntil).getTime() > Date.now(),
  };
}
